import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import {
  PARTICIPANT_SESSION_PATH,
  TEILNEHMER_ANMELDUNG_PATH,
  TEILNEHMER_STIMME_PATH,
  TEILNEHMER_ZUSTAND_PATH,
} from '@mcikm-vote/shared';
import type {
  StimmabgabeAnfrage,
  StimmabgabeAntwort,
  TeilnehmerAnmeldungAnfrage,
  TeilnehmerAnmeldungAntwort,
  TeilnehmerZustand,
} from '@mcikm-vote/shared';

import { apiAnfrage } from '../api/anfrage';
import type { ApiFehler } from '../api/anfrage';
import { verwerfeCsrfWert } from '../api/csrfSpeicher';

/**
 * Anbindung des Teilnehmerbereichs (§9, §13, §19).
 *
 * Der gesamte Anzeigezustand kommt aus **einer** Abfrage: {@link useTeilnehmerZustand}. Welche
 * Ansicht zu zeigen ist, entscheidet der Server (§25.2) — dieses Modul reicht die Entscheidung nur
 * durch. Es gibt hier bewusst keine Abfrage für Ergebnisse oder Beteiligungszahlen; beides ist
 * Bestandteil des Zustands, soweit es der Teilnehmer sehen darf (§15, §16).
 */

export const ZUSTAND_SCHLUESSEL = ['teilnehmer', 'zustand'] as const;

/**
 * Abstand der Zustandsabfrage **ohne** Echtzeitkanal.
 *
 * Dann ist die Abfrage der einzige Weg, auf dem ein Statuswechsel das Gerät erreicht. §22.2
 * verlangt Polling ausdrücklich als Rückfalllösung für blockierte WebSockets — dieser Weg bleibt
 * also dauerhaft bestehen und wird durch den Kanal nicht ersetzt.
 *
 * Drei Sekunden sind ein Kompromiss: §39.2 nennt zwei Sekunden für die Verteilung eines
 * Zustandswechsels; ein kürzerer Abstand wäre für 250 Geräte unnötige Last.
 */
export const ZUSTAND_ABSTAND_MS = 3_000;

/**
 * Abstand der Zustandsabfrage **mit** stehendem Echtzeitkanal.
 *
 * Der Takt wird nicht abgeschaltet, sondern ausgedünnt. Er bleibt aus zwei Gründen bestehen: Ein
 * Ereignis kann verloren gehen, ohne dass die Verbindung abbricht, und §22.2 verlangt das Polling
 * als Rückfalllösung — eine, die nur wirkt, wenn sie auch läuft.
 */
export const ZUSTAND_ABSTAND_ECHTZEIT_MS = 20_000;

/**
 * Abstand der Zustandsabfrage — abhängig von Sitzung und Echtzeitkanal.
 *
 * **Ohne Zustand kein Takt.** Vor der Anmeldung antwortet der Zustandsendpunkt planmäßig mit 401,
 * und die Anmeldeseite hat nach §9.1 ohnehin nichts anzuzeigen, was sich ändern könnte: weder
 * Veranstaltung noch Status noch Ergebnisse. Ein Takt wäre dort vergebliche Last — und er hat
 * zuvor die Eingabe des Zugangscodes unmöglich gemacht, siehe {@link useTeilnehmerZustand}.
 *
 * Nach der Anmeldung liegt ein Zustand vor und der Takt setzt von selbst wieder ein.
 */
export function zustandAbstand(
  zustand: TeilnehmerZustand | undefined,
  echtzeitVerbunden = false,
): number | false {
  if (zustand === undefined) {
    return false;
  }
  return echtzeitVerbunden ? ZUSTAND_ABSTAND_ECHTZEIT_MS : ZUSTAND_ABSTAND_MS;
}

/**
 * Der vollständige Teilnehmerzustand (§19.2).
 *
 * Serverseitig mit `@OhneAktivitaet()` gekennzeichnet: die regelmäßige Abfrage verlängert keine
 * Frist. Für Teilnehmer gibt es ohnehin keinen Ablauf wegen Untätigkeit (§9.4).
 *
 * **Warum der Takt an das Vorhandensein eines Zustands gebunden ist.** Eine Abfrage **ohne Daten**
 * fällt bei jeder Neuabfrage auf `pending` zurück — TanStack Query setzt in `fetchState` bei
 * `data === undefined` ausdrücklich `status: 'pending'`. Vor der Anmeldung war das jede drei
 * Sekunden der Fall, und weil `TeilnehmerBereich` im Ladezustand ein anderes Wurzelelement
 * liefert, hängte React die Anmeldeseite dabei aus und neu ein: der eingetippte Zugangscode war
 * fort, noch bevor er vollständig eingegeben werden konnte.
 *
 * Behoben ist das doppelt — hier durch den ausgesetzten Takt und in `TeilnehmerBereich` dadurch,
 * dass die Ladeanzeige nur noch vor der **ersten** Antwort erscheint.
 */
export function useTeilnehmerZustand(
  aktiv = true,
  echtzeitVerbunden = false,
): UseQueryResult<TeilnehmerZustand, ApiFehler> {
  return useQuery<TeilnehmerZustand, ApiFehler>({
    queryKey: ZUSTAND_SCHLUESSEL,
    queryFn: () =>
      apiAnfrage<TeilnehmerZustand>(TEILNEHMER_ZUSTAND_PATH, { bereich: 'teilnehmer' }),
    enabled: aktiv,
    // Eine fehlende Sitzung ist ein erwarteter Zustand, kein Fehler, der Wiederholung verdient.
    retry: false,
    refetchInterval: (abfrage) => zustandAbstand(abfrage.state.data, echtzeitVerbunden),
    // Nach dem Zurückkehren zum Tab soll der Zustand sofort stimmen (§9.7) — aber auch das erst
    // ab der Anmeldung. Auf der Anmeldeseite bliebe sonst ein Blick auf die Zugangskarte und
    // zurück genug, um die Eingabe zu verwerfen.
    refetchOnWindowFocus: (abfrage) => abfrage.state.data !== undefined,
    staleTime: 0,
  });
}

export function useTeilnehmerAnmelden(): UseMutationResult<
  TeilnehmerAnmeldungAntwort,
  ApiFehler,
  TeilnehmerAnmeldungAnfrage
> {
  const queryClient = useQueryClient();

  return useMutation<TeilnehmerAnmeldungAntwort, ApiFehler, TeilnehmerAnmeldungAnfrage>({
    mutationFn: (anfrage) =>
      apiAnfrage<TeilnehmerAnmeldungAntwort>(TEILNEHMER_ANMELDUNG_PATH, {
        bereich: 'teilnehmer',
        methode: 'POST',
        koerper: anfrage,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ZUSTAND_SCHLUESSEL });
    },
  });
}

export function useTeilnehmerAbmelden(): UseMutationResult<void, ApiFehler, void> {
  const queryClient = useQueryClient();

  return useMutation<void, ApiFehler, void>({
    mutationFn: () =>
      apiAnfrage<void>(PARTICIPANT_SESSION_PATH, { bereich: 'teilnehmer', methode: 'DELETE' }),
    onSettled: () => {
      // Auch bei einem Fehlschlag wird der lokale Zustand verworfen: der Serverzustand ist
      // verbindlich (§40.3), und die nächste Abfrage stellt ihn fest.
      verwerfeCsrfWert('teilnehmer');
      queryClient.removeQueries({ queryKey: ZUSTAND_SCHLUESSEL });
    },
  });
}

/**
 * Stimmabgabe (§13).
 *
 * Nach Erfolg wird der Zustand neu geladen: er entscheidet, dass jetzt „Stimme abgegeben" zu
 * zeigen ist — und er liefert die Abstimmung nicht mehr mit, sodass die gewählte Option nicht
 * mehr angezeigt werden kann (§13.1 Schritt 9).
 */
export function useStimmeAbgeben(): UseMutationResult<
  StimmabgabeAntwort,
  ApiFehler,
  StimmabgabeAnfrage
> {
  const queryClient = useQueryClient();

  return useMutation<StimmabgabeAntwort, ApiFehler, StimmabgabeAnfrage>({
    mutationFn: (anfrage) =>
      apiAnfrage<StimmabgabeAntwort>(TEILNEHMER_STIMME_PATH, {
        bereich: 'teilnehmer',
        methode: 'POST',
        koerper: anfrage,
      }),
    // Ausdrücklich **keine** Wiederholung durch die Bibliothek: eine Stimmabgabe wird nur auf
    // ausdrückliche Bestätigung hin wiederholt, und dann mit derselben Vorgangskennung (§13.3).
    retry: false,
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ZUSTAND_SCHLUESSEL });
    },
  });
}
